import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Grid,
    Avatar,
    Chip,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Alert,
    Paper,
    Stack
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Person as PersonIcon,
    Group as GroupIcon,
    Assignment as AssignmentIcon,
    Launch as LaunchIcon
} from '@mui/icons-material';
import { Campaign } from '../../../../types/Campaign';

interface KickoffProps {
    campaign: Campaign;
}

// Mock data for matches - in real app this would come from API
interface Match {
    id: string;
    mentorName: string;
    menteeName: string;
    status: 'finalized' | 'pending';
    matchDate: string;
}

// Mock data for applications - in real app this would come from API
interface Application {
    id: string;
    applicantName: string;
    role: 'mentor' | 'mentee';
    status: 'pending' | 'approved' | 'rejected';
    submittedDate: string;
}

const mockMatches: Match[] = [
    {
        id: '1',
        mentorName: 'Sarah Johnson',
        menteeName: 'Alex Chen',
        status: 'finalized',
        matchDate: '2024-01-15'
    },
    {
        id: '2',
        mentorName: 'Jennifer Lee',
        menteeName: 'David Park',
        status: 'finalized',
        matchDate: '2024-01-15'
    },
    {
        id: '3',
        mentorName: 'Michael Rodriguez',
        menteeName: 'Emma Wilson',
        status: 'pending',
        matchDate: '2024-01-16'
    }
];

const mockApplications: Application[] = [
    {
        id: '1',
        applicantName: 'John Smith',
        role: 'mentor',
        status: 'pending',
        submittedDate: '2024-01-10'
    },
    {
        id: '2',
        applicantName: 'Lisa Zhang',
        role: 'mentee',
        status: 'pending',
        submittedDate: '2024-01-12'
    },
    {
        id: '3',
        applicantName: 'Robert Kim',
        role: 'mentor',
        status: 'pending',
        submittedDate: '2024-01-14'
    }
];

const Kickoff: React.FC<KickoffProps> = ({
    campaign,
}) => {
    const [isKickingOff, setIsKickingOff] = useState(false);

    const finalizedMatches = mockMatches.filter(match => match.status === 'finalized');
    const pendingMatches = mockMatches.filter(match => match.status === 'pending');
    const unreviewedApplications = mockApplications.filter(app => app.status === 'pending');

    const canKickoff = unreviewedApplications.length === 0 && pendingMatches.length === 0;

    const handleKickoff = async () => {
        if (!canKickoff) return;

        setIsKickingOff(true);
        try {
            // Add kickoff logic here
            console.log('Kicking off campaign:', campaign.campaign_name);
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 2000));
            // Show success message or redirect
        } catch (error) {
            console.error('Failed to kick off campaign:', error);
        } finally {
            setIsKickingOff(false);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Campaign Kickoff
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Review the matching results and application status before launching {campaign.campaign_name}
            </Typography>

            {/* Status Overview */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                <Avatar sx={{ bgcolor: 'success.main', flexShrink: 0 }}>
                                    <GroupIcon />
                                </Avatar>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="h4" sx={{ lineHeight: 1.2 }}>{finalizedMatches.length}</Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            lineHeight: 1.3,
                                            wordBreak: 'break-word',
                                            hyphens: 'auto'
                                        }}
                                    >
                                        Finalized Matches
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                <Avatar sx={{ bgcolor: 'warning.main', flexShrink: 0 }}>
                                    <WarningIcon />
                                </Avatar>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="h4" sx={{ lineHeight: 1.2 }}>{pendingMatches.length}</Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            lineHeight: 1.3,
                                            wordBreak: 'break-word',
                                            hyphens: 'auto'
                                        }}
                                    >
                                        Pending Matches
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                <Avatar sx={{ bgcolor: 'error.main', flexShrink: 0 }}>
                                    <AssignmentIcon />
                                </Avatar>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="h4" sx={{ lineHeight: 1.2 }}>{unreviewedApplications.length}</Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            lineHeight: 1.3,
                                            wordBreak: 'break-word',
                                            hyphens: 'auto'
                                        }}
                                    >
                                        Unreviewed Applications
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                <Avatar sx={{ bgcolor: 'primary.main', flexShrink: 0 }}>
                                    <PersonIcon />
                                </Avatar>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="h4" sx={{ lineHeight: 1.2 }}>{mockMatches.length}</Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            lineHeight: 1.3,
                                            wordBreak: 'break-word',
                                            hyphens: 'auto'
                                        }}
                                    >
                                        Total Matches
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Readiness Check */}
            {!canKickoff && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Campaign Not Ready for Kickoff
                    </Typography>
                    <Typography variant="body2">
                        Please resolve the following issues before launching:
                    </Typography>
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                        {unreviewedApplications.length > 0 && (
                            <li>Review {unreviewedApplications.length} pending application{unreviewedApplications.length !== 1 ? 's' : ''}</li>
                        )}
                        {pendingMatches.length > 0 && (
                            <li>Finalize {pendingMatches.length} pending match{pendingMatches.length !== 1 ? 'es' : ''}</li>
                        )}
                    </ul>
                </Alert>
            )}

            {canKickoff && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Ready for Kickoff!
                    </Typography>
                    <Typography variant="body2">
                        All applications have been reviewed and matches have been finalized. The campaign is ready to launch.
                    </Typography>
                </Alert>
            )}

            <Grid container spacing={3}>
                {/* Finalized Matches */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Finalized Matches ({finalizedMatches.length})
                        </Typography>
                        {finalizedMatches.length > 0 ? (
                            <List>
                                {finalizedMatches.map((match) => (
                                    <ListItem key={match.id} sx={{ px: 0 }}>
                                        <ListItemAvatar>
                                            <Avatar sx={{ bgcolor: 'success.main' }}>
                                                <CheckCircleIcon />
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {match.mentorName}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        →
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {match.menteeName}
                                                    </Typography>
                                                </Stack>
                                            }
                                            secondary={`Finalized on ${new Date(match.matchDate).toLocaleDateString()}`}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No finalized matches yet.
                            </Typography>
                        )}
                    </Paper>
                </Grid>

                {/* Unreviewed Applications */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Unreviewed Applications ({unreviewedApplications.length})
                        </Typography>
                        {unreviewedApplications.length > 0 ? (
                            <List>
                                {unreviewedApplications.map((application) => (
                                    <ListItem key={application.id} sx={{ px: 0 }}>
                                        <ListItemAvatar>
                                            <Avatar sx={{ bgcolor: 'warning.main' }}>
                                                <AssignmentIcon />
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {application.applicantName}
                                                    </Typography>
                                                    <Chip
                                                        label={application.role}
                                                        size="small"
                                                        color={application.role === 'mentor' ? 'primary' : 'secondary'}
                                                    />
                                                </Stack>
                                            }
                                            secondary={`Submitted on ${new Date(application.submittedDate).toLocaleDateString()}`}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                All applications have been reviewed.
                            </Typography>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* Kickoff Button */}
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<LaunchIcon />}
                    onClick={handleKickoff}
                    disabled={!canKickoff || isKickingOff}
                    sx={{ px: 4, py: 1.5 }}
                >
                    {isKickingOff ? 'Launching Campaign...' : 'Launch Campaign'}
                </Button>
            </Box>
        </Box>
    );
};

export default Kickoff;